import { Link } from "react-router-dom";
import type { Article } from "@/types/content";

interface ArticleCardProps {
  article: Article;
  variant?: "vertical" | "horizontal" | "featured";
  rank?: number;
}

function MetaRow({ article, light = false }: { article: Article; light?: boolean }) {
  const color = light ? "text-background-200" : "text-foreground-500";
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${color}`}>
      <span className="flex items-center gap-1">
        <i className="ri-user-3-line" />
        {article.author}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-calendar-line" />
        {article.publishDate}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-time-line" />
        {article.readTime}
      </span>
    </div>
  );
}

export default function ArticleCard({ article, variant = "vertical", rank }: ArticleCardProps) {
  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-background-100"
      >
        <div className="relative h-24 w-32 shrink-0 overflow-hidden rounded-md md:h-28 md:w-40">
          <img
            src={article.cover}
            alt={`${article.title} 现场图片`}
            title={`${article.title} - 看料大瓜现场直击`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-col justify-between">
          <div>
            <div className="flex items-center gap-2">
              {typeof rank === "number" && (
                <span className="font-heading text-base font-black text-accent-600">
                  {String(rank).padStart(2, "0")}
                </span>
              )}
              <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                {article.categoryName}
              </span>
              {article.hot && (
                <span className="flex items-center gap-0.5 text-[11px] text-primary-600">
                  <i className="ri-fire-line" />
                  热议
                </span>
              )}
            </div>
            <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
              {article.title}
            </h3>
          </div>
          <div className="mt-2 hidden sm:block">
            <MetaRow article={article} />
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group relative block cursor-pointer overflow-hidden rounded-lg"
      >
        <div className="h-[420px] w-full md:h-[520px]">
          <img
            src={article.cover}
            alt={`${article.title} 现场图片`}
            title={`${article.title} - 看料大瓜现场直击`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
              {article.categoryName}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-black/40 px-2.5 py-1 text-[11px] text-background-100 backdrop-blur">
              <i className="ri-live-line" />
              现场直击
            </span>
          </div>
          <h3 className="mt-3 font-heading text-2xl font-black leading-snug text-white md:text-3xl">
            {article.title}
          </h3>
          <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-200 md:block">
            {article.excerpt}
          </p>
          <div className="mt-4">
            <MetaRow article={article} light />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="h-44 w-full overflow-hidden md:h-48">
        <img
          src={article.cover}
          alt={`${article.title} 现场图片`}
          title={`${article.title} - 看料大瓜现场直击`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] text-accent-900">
            {article.categoryName}
          </span>
          {article.hot && (
            <span className="flex items-center gap-0.5 text-[11px] text-primary-600">
              <i className="ri-fire-line" />
              热议
            </span>
          )}
        </div>
        <h3 className="mt-3 line-clamp-2 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          {article.title}
        </h3>
        <p className="mt-2.5 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-4 border-t border-background-200 pt-3">
          <MetaRow article={article} />
        </div>
      </div>
    </Link>
  );
}